import React, { useCallback } from 'react';
import ProjectSectionHeader from './ProjectSectionHeader';
import { Box, Grid2 as Grid, Stack, Typography } from '@mui/material';
import { ShowSection } from 'framework/ui/layout';
import { ACCOUNT_PATH } from 'domain/shared/account';
import { EmployeeLinkFieldWithNameLoading, EmployeeInput } from 'domain/shared/employee';
import {
  CloseFormShowButton,
  EditFormShowButton,
  SaveFormShowButton,
  useFormShowContext,
  useFormContext,
  EnumDropdown,
  TextArea,
  TextInput,
} from 'framework/form';
import { LinkField, TextField } from 'framework/ui/field';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  BILLING_TYPE_LABELS,
  BillingType,
  CYCLE_FREQUENCY_LABELS,
  ENGAGEMENT_TYPE_OPTIONS,
  getProjectManager,
  ProjectData,
  REPORT_FREQUENCY_OPTIONS,
  SYSTEM_REPORT_OPTIONS,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import { SwitchToProjectViewButton } from 'domain/shared/project';
import ApprovalTypeSelector from './ApprovalTypeSelector';
import ExternalResourcesList from './ExternalResourcesList';
import { formatDisplayValue } from 'domain/projects/newProject/customerReport/utills';
import { useShowContext } from 'framework/show';
import HealthcheckSystemIssues from './HealthcheckSystemIssues';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';
import ArchiveProjectButton from './ArchiveProjectButton';
import RelativeDateWithTooltip from 'domain/projects/newProject/shared/RelativeDateWithTooltip';
import ApprovalRequiredToggler from './ApprovalRequiredToggler';
import { Tooltip } from 'framework/ui/mui-builder';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface InfoRowProps {
  label: string;
  children: React.ReactNode;
}

const InfoRow = ({ label, children }: InfoRowProps) => (
  <Grid container direction="row">
    <Grid size={6}>
      <Typography color="textSecondary">{label}</Typography>
    </Grid>
    <Grid>{children}</Grid>
  </Grid>
);

const ProjectSection = () => {
  const { isAccessGranted } = useProjectSecurityContext();
  const { isEditMode } = useFormShowContext();
  const { data } = useShowContext<ProjectData>();
  const { data: formData } = useFormContext();

  const { accountData, projectDimension, ...projectData }: ProjectData = data.getData();
  const projectManager = getProjectManager(projectData);
  const isCustomSystem = formData?.settings?.reportSettings?.isSystemReportUsed === 'false';

  const renderActions = useCallback(() => {
    if (
      isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE) ||
      isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.PARTIAL_MANAGE)
    ) {
      return isEditMode ? (
        <>
          <SaveFormShowButton />
          <CloseFormShowButton />
        </>
      ) : (
        <>
          {isAccessGranted(
            ACCESS_INFO_SECTIONS.PROJECT_VIEW_NAVIGATION,
            ACCESS_INFO_ACTIONS.SWITCH_TO_STAFFING_VIEW,
          ) && <SwitchToProjectViewButton isStaffingView projectId={data.id} />}
          <ArchiveProjectButton id={data?.id} isArchived={data?.isArchived} />
          <EditFormShowButton />
        </>
      );
    }
    return null;
  }, [isEditMode, isAccessGranted, data?.id, data?.isArchived]);

  return (
    <ShowSection
      justifyContent="space-between"
      titleId="project-group-title"
      title={<ProjectSectionHeader data={data.getData()} actions={renderActions()} isEdit={isEditMode} />}
    >
      {isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.VIEW) && (
        <Grid container spacing={2} sx={{ pb: 2 }}>
          <Grid size={!isEditMode ? { md: 3, lg: 3, xl: 4, xs: 12 } : 12}>
            <Stack spacing={2}>
              {!isEditMode && <TextField source="description" label="" />}
              {isEditMode && (
                <TextArea
                  source="description"
                  disabled={
                    !(
                      isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE) ||
                      isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.PARTIAL_MANAGE)
                    )
                  }
                  label=""
                  placeholder="Description"
                  rows={4}
                />
              )}
            </Stack>
          </Grid>
          {!isEditMode && isAccessGranted(ACCESS_INFO_SECTIONS.HEALTH, ACCESS_INFO_ACTIONS.VIEW) && (
            <Grid size={{ md: 3, lg: 5, xs: 12 }}>
              <HealthcheckSystemIssues sx={{ mb: 1 }} />
            </Grid>
          )}
          <>
            {isEditMode
              ? [
                  <Grid container size={12} direction="column" key="edit-mode">
                    <Typography variant="h6" color="primary" pb={2}>
                      Project information
                    </Typography>
                    <Grid container direction="row" spacing={2} columns={{ md: 4, sm: 2, xs: 1 }}>
                      <Grid size={1}>
                        <TextInput source="accountData.accountName" label="Account" disabled />
                      </Grid>
                      <Grid size={1}>
                        <TextInput
                          source="accountData.sowId"
                          value={`${accountData?.accountId} - SOW-${accountData?.sowId}`}
                          label="Current SOW"
                          disabled
                        />
                      </Grid>
                      <Grid size={1}>
                        <TextInput
                          source="sensitiveData.originalName"
                          label="Project original name"
                          disabled={!isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE)}
                        />
                      </Grid>
                      <Grid size={1}>
                        <TextInput
                          source="name"
                          label="Project code name"
                          disabled={!isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE)}
                        />
                      </Grid>
                      <Grid size={1}>
                        <EmployeeInput
                          isInitialNameLoad
                          source="accountData.ownerAlias"
                          label="Account manager"
                          disabled
                        />
                      </Grid>
                      <Grid size={1}>
                        <EnumDropdown
                          options={ENGAGEMENT_TYPE_OPTIONS}
                          source="projectDimension.engagementType"
                          label="Engagement type"
                          disabled={!isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE)}
                        />
                      </Grid>
                      <Grid size={1} pl={1}>
                        <ApprovalRequiredToggler
                          source={'settings.forecastSettings.isProjectForecastTrackingEnabled'}
                          label={
                            <Box display="flex" alignItems="center">
                              <Typography>Enable Project Forecast Tracking</Typography>
                              {isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE) && (
                                <Tooltip
                                  title="Defines whether the system will track delivery forecasts (progress, budget, timeline) for the project.
"
                                >
                                  <ErrorOutlineIcon fontSize="small" sx={{ color: 'text.secondary', pl: 1 }} />
                                </Tooltip>
                              )}
                            </Box>
                          }
                          disabled={!isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE)}
                        />
                      </Grid>
                      <Grid size={1} pl={1}>
                        <ApprovalRequiredToggler
                          source={'settings.forecastSettings.isEvmTrackingEnabled'}
                          label={
                            <Box display="flex" alignItems="center">
                              <Typography>Enable EVM Tracking</Typography>
                              {isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE) && (
                                <Tooltip title="Enables advanced Earned Value Management tracking for the project">
                                  <ErrorOutlineIcon sx={{ color: 'text.secondary', pl: 1, size: 'small' }} />
                                </Tooltip>
                              )}
                            </Box>
                          }
                          disabled={!isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE)}
                        />
                      </Grid>
                    </Grid>
                    <Grid container direction="row" alignItems="center" py={2}>
                      <Typography variant="h6" color="primary">
                        Project tools
                      </Typography>
                    </Grid>
                    <ExternalResourcesList
                      disabled={
                        !(
                          isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE) ||
                          isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.PARTIAL_MANAGE)
                        )
                      }
                      hasFullManageAccess={isAccessGranted(
                        ACCESS_INFO_SECTIONS.PROJECT_SUMMARY,
                        ACCESS_INFO_ACTIONS.MANAGE,
                      )}
                      source="resourcesData.externalResources"
                    />
                    <Typography variant="h6" color="primary" py={2}>
                      Project settings
                    </Typography>
                    <Grid container direction="row" spacing={2} columns={{ md: 4, sm: 2, xs: 1 }}>
                      <Grid size={1}>
                        <EnumDropdown
                          source="settings.reportSettings.reportFrequency"
                          label="Customer report frequency"
                          options={REPORT_FREQUENCY_OPTIONS}
                          disabled={
                            !(
                              isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE) ||
                              isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.PARTIAL_MANAGE)
                            )
                          }
                        />
                      </Grid>
                      <Grid size={1}>
                        <EnumDropdown
                          source="settings.reportSettings.isSystemReportUsed"
                          label="Customer Report system"
                          options={SYSTEM_REPORT_OPTIONS}
                          disabled={
                            !(
                              isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE) ||
                              isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.PARTIAL_MANAGE)
                            )
                          }
                        />
                      </Grid>
                      {!isCustomSystem && (
                        <Grid size={1}>
                          <ApprovalTypeSelector
                            disabled={
                              !isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE)
                            }
                            source="settings.reportSettings.approvalType"
                          />
                        </Grid>
                      )}
                      <Grid size={1}>
                        <EnumDropdown
                          disabled={!isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE)}
                          source="settings.cycleActivitySettings.cycleFrequency"
                          label="Cycle management"
                          options={CYCLE_FREQUENCY_LABELS}
                        />
                      </Grid>
                      <Grid size={1}>
                        <EnumDropdown
                          disabled
                          source="budget.billingType"
                          label="Billing type"
                          options={BILLING_TYPE_LABELS}
                        />
                      </Grid>
                    </Grid>
                  </Grid>,
                ]
              : [
                  <Grid size="grow" key="view-mode">
                    <Stack spacing={1}>
                      <InfoRow label="Account">
                        <LinkField
                          linkText={accountData?.accountName || `—`}
                          label=""
                          isInternal
                          value={accountData?.accountId ? `${ACCOUNT_PATH}/${accountData?.accountId}` : `—`}
                        />
                      </InfoRow>
                      <InfoRow label="Billing type">
                        <TextField
                          source="budget.billingType"
                          label=""
                          value={BILLING_TYPE_LABELS[projectData?.budget?.billingType as BillingType]}
                        />
                      </InfoRow>
                      {isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_DIMENSION, ACCESS_INFO_ACTIONS.VIEW) && (
                        <InfoRow label="Engagement type">
                          <TextField
                            source="projectDimension.engagementType"
                            value={
                              projectDimension?.engagementType
                                ? ENGAGEMENT_TYPE_OPTIONS[projectDimension?.engagementType]
                                : EMPTY_VALUE_INDICATOR
                            }
                            label=""
                          />
                        </InfoRow>
                      )}
                      <InfoRow label="Customer report">
                        <TextField
                          source="settings.reportSettings.reportFrequency"
                          label=""
                          value={formatDisplayValue(projectData?.settings?.reportSettings?.reportFrequency)}
                        />
                      </InfoRow>
                      <InfoRow label="Project in progress">
                        <RelativeDateWithTooltip
                          date={projectData?.auditData?.activatedAt ?? projectData?.health?.createdAt}
                          archivingDate={projectData?.auditData?.archivedAt}
                          hideAgo
                          showIn
                        />
                      </InfoRow>
                      <InfoRow label="Project manager">
                        <EmployeeLinkFieldWithNameLoading
                          ignoredNameParts={['middleName', 'maidenName']}
                          source="projectManager"
                          value={projectManager}
                          label=""
                        />
                      </InfoRow>
                    </Stack>
                  </Grid>,
                ]}
          </>
        </Grid>
      )}
    </ShowSection>
  );
};

export default ProjectSection;
